/**
 * Damage, combo scaling, counter-hit and punish maths. Pure functions so
 * `tools/combat-test.mjs` and `tests/unit/damage.test.js` can assert them directly without
 * running a simulation. Three-free.
 *
 * Model
 * -----
 * The first hit of a combo lands at full damage and sets the combo's scale from the
 * starter's `starterValue`. Every subsequent hit is multiplied by the running scale, and the
 * scale then decays by that move's `cost`. Scale is floored at `minScale` so a long combo
 * always trends toward zero return without ever reaching it — this is what makes long
 * juggles spectacular but not lethal.
 *
 * Counter-hit and punish multipliers apply BEFORE scaling, so a counter-hit starter is worth
 * more for the whole combo, not just its first hit — which is the reward that makes Kestrel's
 * whiff-punish game matter.
 */

import { MIN_COMBO_SCALE } from '../core/constants.js';

/** Extra damage multiplier for hitting a fighter in recovery frames. */
export const PUNISH_DAMAGE_MUL = 1.1;
/** Damage multiplier applied to a juggled (airborne) defender, per juggle hit. */
export const JUGGLE_DECAY = 0.94;
/** Chip damage can never kill; a blocking fighter is always left with at least this much. */
export const CHIP_FLOOR = 1;

/**
 * @typedef {object} HitContext
 * @property {boolean} counter    defender was in a counter-hit state
 * @property {boolean} punish     defender was in recovery
 * @property {boolean} airborne   defender was airborne
 * @property {number}  juggleCount how many juggle hits the defender has already taken
 */

/**
 * Computes the damage a hit deals, plus the resulting combo scale.
 *
 * @param {object} attacker FighterState
 * @param {object} move normalised move definition
 * @param {HitContext} hc
 * @returns {{ damage: number, rawDamage: number, scaleUsed: number, nextScale: number,
 *            counterBonus: number }}
 */
export function computeHitDamage(attacker, move, hc) {
  const raw = move.damage;
  const isFirstHit = attacker.comboHits === 0;

  let mul = 1;
  let counterBonus = 0;
  if (hc.counter) {
    mul *= move.counterHit.damageMul;
    counterBonus = raw * (move.counterHit.damageMul - 1);
  }
  if (hc.punish) mul *= PUNISH_DAMAGE_MUL;

  // The starter's value applies from the SECOND hit onward; the opener always lands clean.
  const scaleUsed = isFirstHit ? 1 : attacker.comboScale;

  // Airborne hits decay slightly faster than grounded ones so juggles stay finite.
  const juggleMul = hc.airborne ? Math.pow(JUGGLE_DECAY, Math.max(0, hc.juggleCount)) : 1;

  const damage = Math.max(1, Math.round(raw * mul * scaleUsed * juggleMul));

  // Advance the scale for the next hit.
  let nextScale;
  if (isFirstHit) {
    nextScale = clampScale(move.scaling.starterValue, move.scaling.minScale);
  } else {
    nextScale = clampScale(attacker.comboScale * (1 - move.scaling.cost), move.scaling.minScale);
  }

  return { damage, rawDamage: raw, scaleUsed, nextScale, counterBonus: Math.round(counterBonus) };
}

function clampScale(v, minScale) {
  const floor = Math.max(MIN_COMBO_SCALE, minScale);
  return v < floor ? floor : v > 1 ? 1 : v;
}

/**
 * Applies a connected hit to the attacker's combo tracker.
 * Must be called exactly once per confirmed hit, after computeHitDamage.
 */
export function applyComboProgress(attacker, move, damage, nextScale, tick) {
  if (attacker.comboHits === 0) attacker.comboStarter = move.scaling.starterValue;
  attacker.comboHits++;
  attacker.comboDamage += damage;
  attacker.comboScale = nextScale;
  attacker.lastHitTick = tick;
  return attacker.comboHits;
}

/**
 * Chip damage on block, with the guarantee that chip can never finish a round —
 * a player who blocks correctly always survives to keep blocking.
 */
export function computeChipDamage(defender, move) {
  const stats = defender.stats;
  const chip = Math.round(move.chipDamage * stats.guardChipMul);
  if (chip <= 0) return 0;
  const survivable = Math.max(0, defender.health - CHIP_FLOOR);
  return Math.min(chip, survivable);
}

/**
 * Guard meter drain on block. Heavier moves and unblocked-string pressure drain faster.
 * Returns the new guard meter and whether the guard broke.
 */
export function computeGuardDrain(defender, move, blockstunTicks) {
  const stats = defender.stats;
  // Drain scales with the move's commitment: a heavy that leaves you at -12 on block
  // should also threaten the guard, otherwise blocking is strictly free.
  const base = 3 + move.damage * 0.16 + blockstunTicks * 0.08;
  const stanceMul = defender.stance === 'flow' ? 1 / stats.flowGuardMul : 1;
  const drain = base * stanceMul;
  const next = Math.max(0, defender.guardMeter - drain);
  return { guardMeter: next, guardBreak: next <= 0 && defender.guardMeter > 0, drain };
}

/**
 * Hitstun in ticks, including the counter-hit bonus and juggle adjustments.
 */
export function computeHitstun(move, hc) {
  let ticks = move.tHitstun;
  if (hc.counter) ticks += move.tCounterHitstunBonus;
  // Airborne defenders stay in hitstun until they land; the value is a floor, not a cap.
  if (hc.airborne) ticks = Math.max(ticks, 12);
  return ticks;
}

/**
 * Hitstop (freeze) in ticks. Both fighters freeze for the same duration, which is what makes
 * an impact read as a collision rather than two independent animations.
 */
export function computeHitstop(move, hc, table) {
  if (move.onHit.hitstop > 0) return move.onHit.hitstop;
  if (hc.counter) return table.counter;
  if (move.isThrow) return table.throw;
  if (move.damage >= 28) return table.heavy;
  if (move.damage >= 16) return table.medium;
  return table.light;
}

/**
 * Frame advantage after a hit or block, in f60 frames. Positive means the attacker acts first.
 * Derived — never authored — so the number training mode shows is the number the simulation
 * actually produces.
 */
export function frameAdvantage(move, blocked) {
  return blocked ? move.blockAdvantage : move.hitAdvantage;
}

/**
 * Meter gain for both fighters from one interaction.
 * Kestrel's Read builds from reads (counter-hits, parries, punishes).
 * Bulwark's Grit builds from damage taken, so a losing Bulwark gains options.
 */
export function computeMeterGain(fighter, event, amount = 0) {
  const stats = fighter.stats;
  switch (event) {
    case 'counter-hit':
      return stats.meterOnCounterHit;
    case 'parry':
      return stats.meterOnParry;
    case 'whiff-punish':
      return stats.meterOnWhiffPunish;
    case 'damage-taken':
      return amount * stats.meterOnDamageTaken;
    default:
      return 0;
  }
}

export function addMeter(fighter, amount) {
  if (amount <= 0) return fighter.meter;
  fighter.meter = Math.min(fighter.meterMax, fighter.meter + amount);
  return fighter.meter;
}

export function spendMeter(fighter, amount) {
  if (fighter.meter < amount) return false;
  fighter.meter -= amount;
  return true;
}

/**
 * Knockback velocity applied to the defender, m/s along the combat axis.
 * Heavier fighters move less; this is the main reason Bulwark is harder to push out.
 */
export function computeKnockback(defender, move, hc) {
  const massScale = 78 / defender.stats.mass; // 78 kg is the notional reference mass
  let vx = (move.onHit.knockbackX || move.pushbackHit * 9) * massScale;
  if (hc.counter) vx *= 1.15;
  if (hc.airborne) vx *= 1.1;
  return vx;
}

/**
 * Sanity report used by tools/combat-test.mjs: does this move table contain a combo that
 * could deal more than `limit` fraction of a health bar from one starter at worst case?
 */
export function theoreticalMaxCombo(moves, startId, maxHits = 12) {
  const start = moves.byId.get(startId);
  if (!start) return null;
  let scale = start.scaling.starterValue;
  let total = start.damage;
  let hits = 1;
  // Greedy: repeatedly take the highest post-scaling damage move that can extend a juggle.
  const extenders = moves.all.filter((m) => m.juggleOnly || m.onHit.launch > 0 || m.category === 'strike');
  while (hits < maxHits) {
    let best = null;
    let bestDmg = 0;
    for (const m of extenders) {
      const d = m.damage * scale;
      if (d > bestDmg) {
        bestDmg = d;
        best = m;
      }
    }
    if (!best || bestDmg < 1) break;
    total += Math.round(bestDmg);
    scale = clampScale(scale * (1 - best.scaling.cost), best.scaling.minScale);
    hits++;
  }
  return { startId, hits, total, finalScale: scale };
}
